<template>
  <div class="completed-steps">
    <StepHeader
      :title="title"
      :theme="theme"
    />

    <StepContent
      width="sm"
      :theme="theme"
    >
      <div class="max-w-500 m-auto">
        <p class="text-center">
          {{ subtitle }}
        </p>
        <AppProgressCircleBar
          :theme="theme"
          :step-labels="stepLabelsComputed"
        />
      </div>
    </StepContent>

    <StepFooter hide-mobile>
      <div>
        <AppButton
          :theme="theme"
          @click="handleContinue"
        >
          Take Me Back
        </AppButton>
      </div>

      <button
        class="complete-steps__skip"
        :class="textColorClass()"
        @click="handleSubmit"
      >
        {{ submitText }}

        <AppSpinner
          v-if="submitting"
          class="mx-auto mt-20"
          color="#009DBD"
        />
      </button>

      <Benefits
        v-if="showBenefits"
        :theme="theme"
      />
    </StepFooter>

    <Benefits
      v-if="showBenefits"
      :theme="theme"
      mobile
    />
    <FreshChat />
  </div>
</template>

<script>
import StepHeader from '@/components/common/step-layout/StepHeader'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import AppButton from '@/components/common/buttons/AppButton'
import AppProgressCircleBar from '@/components/common/progress/AppProgressCircleBar'
import submitLoans from '@/mixins/submit/submit-loans'
import navigation from '@/mixins/navigation'
import AppSpinner from '@/components/common/spinners/AppSpinner'
import Benefits from '@/components/steps/common/Benefits'
import theme from '@/mixins/theme'
import * as LEAD_TYPE from '@/constants/lead-type'
import FreshChat from '@/components/widgets/freshchat/FreshChat'

export default {
  name: 'CompleteSkippedSteps',

  // Components
  components: {
    StepHeader,
    StepContent,
    StepFooter,
    AppButton,
    AppProgressCircleBar,
    FreshChat,
    AppSpinner,
    Benefits
  },

  // Mixins
  mixins: [
    submitLoans,
    navigation,
    theme
  ],

  props: {
    showBenefits: {
      type: Boolean,
      default: true
    },

    stepLabels: {
      type: [Array, Function],
      required: true
    }
  },

  // Computed
  computed: {
    firstName () {
      const firstName = this.$store.state.customer.first_name
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    },

    title () {
      return `You're so close, ${this.firstName}!`
    },

    subtitle () {
      const leadType = this.$route.matched[0].meta.leadType

      switch (leadType) {
        case LEAD_TYPE.FOUNDATION:
        case LEAD_TYPE.PL_DECLINED:
          return 'You need to complete the following steps before you can get approved for The Foundation.'

        default:
          return 'You need to complete the following steps before you can get approved for your loan.'
      }
    },

    dataLayerObject () {
      return {
        event: `spring_${this.eventPrefix}_take_me_back_click`
      }
    },

    submitIfReady () {
      const submitIfReady = this.$route.meta.submitIfReady
      return typeof submitIfReady === 'function' ? submitIfReady() : submitIfReady
    },

    submitText () {
      return this.submitIfReady ? 'Submit Now and Upload Later' : 'Upload Later'
    },

    stepLabelsComputed () {
      return typeof this.stepLabels === 'function' ? this.stepLabels() : this.stepLabels
    }
  },

  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    },

    $_navigation_triggerSubmitNow: async function (val) {
      if (val) await this.$_loans_submitLeadIfReady(true)
      this.$store.dispatch('setTriggerSubmitNow', false)
    }
  },

  // Methods
  methods: {
    handleContinue () {
      this.$store.dispatch('setUploadLater', false)
      this.$store.dispatch('setLinearDocumentSteps', false)
      this.$_navigation_handleNext(this.dataLayerObject)
    },

    handleSubmit () {
      if (this.submitIfReady) return this.$_loans_submitLeadIfReady(true)
      this.$store.dispatch('setUploadLater', true)
      this.$_navigation_handleNext()
    }
  }
}
</script>

<style scoped lang="scss">
.complete-steps {
  &__skip {
    @apply block underline font-semibold mt-20 mb-50 mx-auto text-center;
  }
}
</style>
